import { View, Text, Pressable } from 'react-native';
import { MessageCircle, ArrowRight, Sparkles, X } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { FadeInUp } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import type { useSystemThemeColors } from '@/lib/useThemeColors';

interface InterviewOnboardingCardProps {
  colors: ReturnType<typeof useSystemThemeColors>;
  onPress: () => void;
  /** Headline. Founder-friendly — never "interview"/"assessment". */
  title?: string;
  /** Supporting copy. Leads with the low-friction promise (~10 min, no docs). */
  description?: string;
  /** Label on the primary action pill. */
  ctaLabel?: string;
  /** Pill in the top-right (e.g. "Recommended"). */
  badgeLabel?: string;
  /** When provided, renders a dismiss control (post-onboarding prompt use). */
  onDismiss?: () => void;
  /** Stagger delay for the entrance animation. */
  delay?: number;
}

/**
 * The recommended, low-friction "have a quick chat" path into the scoring
 * interview. Shared by the onboarding data-input choice (88-04 T1) and the
 * post-onboarding trigger on the complete screen (88-04 T2).
 *
 * Copy is deliberately methodology-hidden (87-05): it reads as a warm, short
 * conversation, never as an "interview" or "assessment".
 */
export function InterviewOnboardingCard({
  colors,
  onPress,
  title = 'Have a quick chat',
  description = "About 10 minutes, no documents needed. We'll ask a few questions and build your readiness score.",
  ctaLabel = 'Start the conversation',
  badgeLabel = 'Recommended',
  onDismiss,
  delay = 0,
}: InterviewOnboardingCardProps) {
  return (
    <Animated.View entering={FadeInUp.delay(delay).duration(500)}>
      <AnimatedPressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${description}`}
        style={{ borderRadius: 20 }}
      >
        <LinearGradient
          colors={[colors.primary[50], colors.surface.primary]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            borderRadius: 20,
            padding: 20,
            borderWidth: 1.5,
            borderColor: colors.primary[200],
            overflow: 'hidden',
          }}
        >
          {/* Top row: icon + badge + optional dismiss */}
          <View className="flex-row items-center justify-between mb-4">
            <View
              className="w-12 h-12 rounded-2xl items-center justify-center"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <MessageCircle size={24} color={colors.text.inverse} />
            </View>

            <View className="flex-row items-center gap-2">
              <View
                className="flex-row items-center gap-1 px-2.5 py-1 rounded-full"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Sparkles size={12} color={colors.text.inverse} />
                <Text
                  numberOfLines={1}
                  style={{ color: colors.text.inverse }}
                  className="text-xs font-bold"
                >
                  {badgeLabel}
                </Text>
              </View>

              {onDismiss ? (
                <Pressable
                  onPress={onDismiss}
                  hitSlop={10}
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss"
                  className="w-7 h-7 rounded-full items-center justify-center active:opacity-60"
                  style={{ backgroundColor: colors.surface.tertiary }}
                >
                  <X size={15} color={colors.text.tertiary} />
                </Pressable>
              ) : null}
            </View>
          </View>

          {/* Title */}
          <Text
            numberOfLines={2}
            style={{ color: colors.text.primary }}
            className="text-lg font-bold mb-1.5"
          >
            {title}
          </Text>

          {/* Description */}
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm leading-relaxed mb-4"
          >
            {description}
          </Text>

          {/* Primary CTA pill */}
          <View
            className="flex-row items-center justify-center gap-2 py-3 rounded-2xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text
              numberOfLines={1}
              style={{ color: colors.text.inverse }}
              className="text-sm font-semibold"
            >
              {ctaLabel}
            </Text>
            <ArrowRight size={18} color={colors.text.inverse} />
          </View>
        </LinearGradient>
      </AnimatedPressable>
    </Animated.View>
  );
}

export default InterviewOnboardingCard;
